import React, { useContext, useRef } from 'react';
import { useNavigate } from 'react-router';

import { Input, Switch } from 'antd';

import { MODELS_WALL_QUICKFILTER_DEFAULT, hasQuickFiltersSet } from '@configs/WALLS_CONFIG';

import { useLayoutContext } from '@contexts/LayoutContext';
import { UserContext } from '@contexts/UserContext';

import { useActiveShowSeasons } from '@hooks/API/ShowSeasons/UseShowSeasons';

import {
    useContactsSelector,
    useContactsSelectorDispatch,
} from '@components/ContactsSelector/Common/ContactsSelectorContext';
import {
    MOBILE_FILTERS_NESTED_ZINDEX,
    QuickFilterContainer,
    QuickFilterContainerMobile,
} from '@components/Filters/FilterForm/FilterFormUI';
import DepartmentSelector from '@components/Gui/DepartmentSelector';
import ShowSeasonSelector from '@components/Gui/ShowSeasonSelector';
import { NwLinkButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import RadioButtonGroup from '@components/Gui/NwForm/RadioButtonGroup';
import NwIcon from '@components/Gui/NwIcon';
import TagFilterPicker from '@components/TagPicker/TagFilterPicker';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const ModelQuickFilters = ({ onAfterChangeFilters, isMini }) => {
    const { selection, context } = useContactsSelector();
    const cs_dispatch = useContactsSelectorDispatch();
    const { quickFilters } = selection;
    const { scope } = context;

    const { currentUser } = useContext(UserContext);
    const activeDepartments = currentUser.ActiveContext.activeDepartments;

    const navigate = useNavigate();
    const nameRef = useRef();

    const layoutContext = useLayoutContext();
    const { breakpoints, isMobile } = layoutContext;
    // sotto i 1024px questi filtri vivono dentro il pannello filtri a tutta pagina:
    // i drawer che aprono devono stargli sopra
    const inMobileFiltersPanel = breakpoints < 3;
    const { data: activeShowSeasons } = useActiveShowSeasons();

    const sortOptions = [
        { label: 'Name', value: 'Name' },
        { label: 'ID', value: 'ID' },
        { label: 'Agency', value: 'MotherAgency' },
    ];

    const handleChangeFilter = (name, value) => {
        cs_dispatch({
            type: 'ChangeQuickFilters',
            quickFilters: {
                ...quickFilters,
                [name]: value,
            },
        });
        if (onAfterChangeFilters) {
            onAfterChangeFilters();
        }
    };

    const handleResetQuickFilters = () => {
        cs_dispatch({
            type: 'ChangeQuickFilters',
            quickFilters: { ...MODELS_WALL_QUICKFILTER_DEFAULT },
        });
        if (onAfterChangeFilters) {
            onAfterChangeFilters();
        }
    };

    // Versione ridotta: solo per la striscia mini (wall in sidebar). Nel pannello filtri
    // mobile c'è spazio verticale per i quick filters completi.
    if (isMobile && isMini) {
        return (
            <QuickFilterContainerMobile>
                <div className="quick-filter-item">
                    <Switch
                        size="small"
                        checked={quickFilters.onlymine}
                        onChange={(value) => {
                            handleChangeFilter('onlymine', value);
                        }}
                    />
                    <span className="after">mine</span>
                </div>
                <div className="quick-filter-item">
                    <RadioButtonGroup
                        value={quickFilters.intown}
                        onChange={(value) => {
                            handleChangeFilter('intown', value);
                        }}
                        size="small"
                        options={[
                            { label: 'IN', value: 'in' },
                            { label: 'OUT', value: 'out' },
                            { label: 'ALL', value: 'all' },
                        ]}
                    />
                </div>
                <div className="quick-filter-item">
                    <RadioButtonGroup
                        value={quickFilters.gender}
                        size="small"
                        onChange={(value) => {
                            handleChangeFilter('gender', value);
                        }}
                        defaultValue="All"
                        options={[
                            { label: '', value: 'female', icon: byPrefixAndName.fasl['venus'] },
                            { label: '', value: 'male', icon: byPrefixAndName.fasl['mars'] },
                            { label: '', value: 'non-binary', icon: byPrefixAndName.fasl['mars-and-venus'] },
                            { label: '', value: 'all', icon: byPrefixAndName.fasl['asterisk'] },
                        ]}
                    />
                </div>
            </QuickFilterContainerMobile>
        );
    }

    return (
        <QuickFilterContainer $isMini={isMini}>
            {!isMini && <NwDivider>quick search</NwDivider>}
            {hasQuickFiltersSet(quickFilters, MODELS_WALL_QUICKFILTER_DEFAULT) && (
                <div className="quick-filter-line">
                    <NwLinkButton
                        icon={byPrefixAndName.fass['undo']}
                        label="reset quick filters"
                        onClick={handleResetQuickFilters}
                    />
                </div>
            )}
            {!isMobile && (
                <div className="quick-filter-line">
                    <Input
                        ref={nameRef}
                        allowClear
                        autoComplete="off"
                        placeholder="Filter by name"
                        prefix={<NwIcon icon={byPrefixAndName.fasl['search']} />}
                        onChange={(e) => {
                            handleChangeFilter('nameFilter', e.target.value);
                        }}
                        value={quickFilters.nameFilter}
                    />
                </div>
            )}
            <div className="quick-filter-line">
                <div className="quick-filter-item">
                    <Switch
                        size="small"
                        checked={quickFilters.onlymine}
                        onChange={(value) => {
                            handleChangeFilter('onlymine', value);
                        }}
                    />
                    <span className="after">mine only</span>
                </div>
                {isMini && !isMobile && (
                    <div className="quick-filter-item">
                        <Switch
                            size="small"
                            checked={quickFilters.showFeaturesQuickFilters}
                            disabled={!quickFilters.showFeaturesQuickFilters}
                            onChange={(val) => {
                                if (!val) {
                                    cs_dispatch({
                                        type: 'ChangeQuickFilters',
                                        quickFilters: {
                                            ...quickFilters,
                                            showFeaturesQuickFilters: false,
                                            featuresQuickFilters: {},
                                        },
                                    });
                                    if (onAfterChangeFilters) {
                                        onAfterChangeFilters();
                                    }
                                }
                            }}
                        />
                        <span className={`after${quickFilters.showFeaturesQuickFilters ? ' highlighted' : ' muted'}`}>
                            features filtered
                        </span>
                    </div>
                )}
            </div>
            {activeDepartments.length > 0 && (
                <div className="quick-filter-line">
                    <div className="quick-filter-item">
                        <DepartmentSelector
                            emptyValue={true}
                            allowClear
                            value={quickFilters.departmentFilter}
                            onChange={(val) => {
                                handleChangeFilter('departmentFilter', val);
                            }}
                        />
                    </div>
                </div>
            )}
            {activeShowSeasons?.length > 0 && (
                <div className="quick-filter-line">
                    <div className="quick-filter-item">
                        <ShowSeasonSelector
                            emptyValue={true}
                            allowClear
                            value={quickFilters.showSeasonFilter}
                            onChange={(val) => {
                                handleChangeFilter('showSeasonFilter', val);
                            }}
                        />
                    </div>
                </div>
            )}
            <div className="quick-filter-line">
                <div className="quick-filter-item">
                    <TagFilterPicker
                        itemType="Model"
                        selectedOptions={quickFilters.tagFilter}
                        filterMode={quickFilters.tagFilterMode}
                        zIndex={inMobileFiltersPanel ? MOBILE_FILTERS_NESTED_ZINDEX : undefined}
                        onChange={(_, _tagFilter) => {
                            handleChangeFilter('tagFilter', _tagFilter);
                        }}
                        onFilterModeChange={(mode) => {
                            handleChangeFilter('tagFilterMode', mode);
                        }}
                    />
                </div>
            </div>
            <div className="quick-filter-line">
                <div className="quick-filter-item">
                    <RadioButtonGroup
                        value={quickFilters.intown}
                        onChange={(value) => {
                            handleChangeFilter('intown', value);
                        }}
                        size="small"
                        options={[
                            { label: 'IN', value: 'in' },
                            { label: 'OUT', value: 'out' },
                            { label: 'ALL', value: 'all' },
                        ]}
                    />
                </div>
                <div className="quick-filter-item">
                    <RadioButtonGroup
                        value={quickFilters.gender}
                        size="small"
                        onChange={(value) => {
                            handleChangeFilter('gender', value);
                        }}
                        defaultValue="All"
                        options={[
                            { label: '', value: 'female', icon: byPrefixAndName.fasl['venus'] },
                            { label: '', value: 'male', icon: byPrefixAndName.fasl['mars'] },
                            { label: '', value: 'non-binary', icon: byPrefixAndName.fasl['mars-and-venus'] },
                            { label: '', value: 'all', icon: byPrefixAndName.fasl['asterisk'] },
                        ]}
                    />
                </div>
            </div>
            {scope !== 'accounting-search' && !isMini && !isMobile && (
                <div className="quick-filter-line">
                    <div className="quick-filter-item">
                        <Switch
                            size="small"
                            checked={quickFilters.showFeaturesQuickFilters}
                            onChange={(val) => {
                                cs_dispatch({
                                    type: 'ChangeQuickFilters',
                                    quickFilters: {
                                        ...quickFilters,
                                        showFeaturesQuickFilters: val,
                                        featuresQuickFilters: {},
                                    },
                                });
                                if (onAfterChangeFilters) {
                                    onAfterChangeFilters();
                                }
                                if (!val) {
                                    navigate('/models');
                                }
                            }}
                        />
                        <span className="after">filter list on features</span>
                    </div>
                </div>
            )}
            {isMini && !isMobile && (
                <div className="quick-filter-line">
                    <div className="quick-filter-item">
                        <span className="before">sort by</span>
                        <RadioButtonGroup
                            value={quickFilters.sort}
                            onChange={(value) => handleChangeFilter('sort', value)}
                            size="small"
                            options={sortOptions}
                        />
                    </div>
                </div>
            )}
        </QuickFilterContainer>
    );
};

export default ModelQuickFilters;
